/**
 * @fileoverview Redux selectors for application settings.
 */

const getGeneralSettings = (state) => {
    if (!state || typeof state !== 'object') return undefined
    const generalSettings = state?.inputData?.present?.generalSettings
    if (!generalSettings || typeof generalSettings !== 'object') return undefined
    return generalSettings
}

export const selectDownloadType = (state) => {
    const generalSettings = getGeneralSettings(state)
    const value = generalSettings?.downloadType
    return typeof value === 'string' ? value : undefined
}

export const selectTimeSetting = (settingName, tableName) => (state) => {
    if (typeof settingName !== 'string' || !settingName) return undefined
    const generalSettings = getGeneralSettings(state)
    const time = generalSettings?.time
    if (!time || typeof time !== 'object') return undefined

    if (!tableName) {
        return time?.[settingName]
    }
    const table = time?.[tableName]
    if (!table || typeof table !== 'object') return undefined
    return table?.[settingName]
}

/**
 * Selector factory for a value under `generalSettings.mapping`
 * (e.g. `mapMode`, `coordinateSystem`).
 *
 * @param {string} settingName - Key within the mapping settings.
 * @returns {(state: object) => *} Selector returning the value, or undefined
 *   when the state path or setting is missing.
 */
export const selectMappingSetting = (settingName) => (state) => {
    if (typeof settingName !== 'string' || !settingName) return undefined
    const generalSettings = getGeneralSettings(state)
    const mapping = generalSettings?.mapping
    if (!mapping || typeof mapping !== 'object') return undefined
    return mapping?.[settingName]
}

/**
 * Selector that determines whether the app is currently running in "example" mode.
 *
 * @param {object} state - Redux state.
 * @returns {boolean} True when `activeMode === 'example'`, otherwise false.
 */
export const selectInExampleMode = (state) => {
    if (!state || typeof state !== 'object') return false

    const activeMode = state?.inputData?.present?.activeMode
    if (typeof activeMode !== 'string') return false

    return activeMode === 'example'
}

/**
 * Slug of the example scenario currently loaded into the workspace, or null for
 * a user's own scenario. Set when an example is loaded; used to look its
 * colour-dimension metadata up in the catalog (for the map legend).
 *
 * @param {object} state - Redux state.
 * @returns {string|null}
 */
export const selectActiveExampleSlug = (state) => {
    const generalSettings = getGeneralSettings(state)
    return generalSettings?.scenario?.exampleSlug ?? null
}


/**
 * Selector factory for retrieving a value from `generalSettings.costs`.
 *
 * Supports up to three levels of nesting:
 * `costs[settingName]`
 * `costs[settingName][subSettingName]`
 * `costs[settingName][subSettingName][subSubSettingName]`
 *
 * Notes:
 * - Returns `undefined` when inputs are invalid or the path does not exist.
 * - Accepts legacy `props.subSubsettingName` (typo) as a fallback for backwards compatibility.
 *
 * @param {object} props - Selector options.
 * @param {string} props.settingName - Top-level cost setting key.
 * @param {string} [props.subSettingName] - Second-level key.
 * @param {string} [props.subSubSettingName] - Third-level key.
 * @param {string} [props.subSubsettingName] - Legacy alias for `subSubSettingName`.
 * @returns {(state: object) => any} Selector function.
 */
export const selectCostSetting =
    (props = {}) =>
    (state) => {
        if (!props || typeof props !== 'object') return undefined

        const settingName = props.settingName
        const subSettingName = props.subSettingName
        const subSubSettingName = props.subSubSettingName || props.subSubsettingName

        if (typeof settingName !== 'string' || !settingName) return undefined
        if (subSettingName != null && (typeof subSettingName !== 'string' || !subSettingName))
            return undefined
        if (
            subSubSettingName != null &&
            (typeof subSubSettingName !== 'string' || !subSubSettingName)
        )
            return undefined

        const costs = state?.inputData?.present?.generalSettings?.costs
        if (!costs || typeof costs !== 'object') return undefined

        const level1 = costs?.[settingName]
        if (level1 == null) return undefined
        if (!subSettingName) return level1

        const level2 = level1?.[subSettingName]
        if (level2 == null) return undefined
        if (!subSubSettingName) return level2

        return level2?.[subSubSettingName]
    }
/**
 * Selector for `generalSettings.tableLabels` (the per-scenario table-label
 * choice). Returns the raw settings object — resolve it to concrete strings
 * with `resolveTableLabels` in store/functions/tableSettingsOverrides.js,
 * which memoizes by value so the pack is referentially stable.
 *
 * @param {object} state - Redux state.
 * @returns {object|undefined} The tableLabels settings, or undefined when the
 *   state predates the setting (resolves to the default pack downstream).
 */
export const selectTableLabels = (state) => {
    const generalSettings = getGeneralSettings(state)
    const value = generalSettings?.tableLabels
    return (value && typeof value === 'object') ? value : undefined
}
